import { useLocation } from 'react-router-dom';
import React from 'react';
import { QueryParameters, QueryParametersValue } from './types';
import { UnBoxArrayType } from '~/utils';
import { Context, useContext } from './context';
import { SchemaHolder } from './schema';

export function useSchema<StaticProps, SearchQuery, Parameters extends QueryParameters>(props: {
  staticProps: StaticProps;
  defaultParameters: Parameters;
  schemaHolder: SchemaHolder<StaticProps, SearchQuery, Parameters>;
  context: Context<Parameters>;
}) {
  type ParamKey = keyof Parameters;
  const { search } = useLocation();
  const { parameters } = useContext(props.context);

  const urlSearchParams = React.useMemo(() => new URLSearchParams(search), [search]);

  const getParameter = React.useCallback(
    <K extends ParamKey>(name: K) => props.schemaHolder.get(name),
    [props.schemaHolder]
  );
  const getDefaultValue = React.useCallback(
    <K extends ParamKey>(name: K) =>
      props.schemaHolder.get(name).getDefaultValue({ defaultParameters: props.defaultParameters }),
    [props.defaultParameters, props.schemaHolder]
  );
  const getValue = React.useCallback(
    <K extends ParamKey>(name: K) =>
      props.schemaHolder.get(name).getValue({ staticProps: props.staticProps, parameters }),
    [parameters, props.schemaHolder, props.staticProps]
  );
  const convertValueToUrlQueryString = React.useCallback(
    <K extends ParamKey>(name: K) => {
      const parameter = props.schemaHolder.get(name);
      if (!parameter.toUrlSearchParams) return null;

      const value = getValue(name);
      const defaultValue = getDefaultValue(name);
      return parameter.toUrlSearchParams({
        defaultValue,
        value,
        parameters,
        staticProps: props.staticProps,
      });
    },
    [props.schemaHolder, getValue, getDefaultValue, props.staticProps, parameters]
  );
  const convertUrlQueryStringToParam = React.useCallback(
    <K extends ParamKey>(name: K) => {
      const parameter = props.schemaHolder.get(name);
      if (!parameter.fromUrlSearchParams) return null;

      const defaultValue = getDefaultValue(name);
      return parameter.fromUrlSearchParams({ defaultValue, staticProps: props.staticProps, urlSearchParams });
    },
    [props.schemaHolder, getDefaultValue, props.staticProps, urlSearchParams]
  );
  const convertValueToSearchQuery = React.useCallback(
    <K extends ParamKey>(name: K) => {
      const parameter = props.schemaHolder.get(name);
      if (!parameter.toSearchQuery) return null;

      const value = getValue(name);
      const defaultValue = getDefaultValue(name);
      return parameter.toSearchQuery({
        defaultValue,
        value,
        parameters,
        staticProps: props.staticProps,
      });
    },
    [props.schemaHolder, getValue, getDefaultValue, props.staticProps, parameters]
  );
  const sanitizeQueryParameter = React.useCallback(
    <K extends ParamKey>(name: K) => {
      const parameter = props.schemaHolder.get(name);
      if (!parameter.sanitize) return null;

      const value = getValue(name);
      const defaultValue = getDefaultValue(name);
      return parameter.sanitize({
        defaultValue,
        value,
        parameters,
        staticProps: props.staticProps,
      });
    },
    [props.schemaHolder, getValue, getDefaultValue, props.staticProps, parameters]
  );

  const removeQueryParameter = React.useCallback(
    <K extends ParamKey>(name: K, value: UnBoxArrayType<QueryParametersValue<Parameters, K>>) => {
      const parameter = props.schemaHolder.get(name);
      if (!parameter.removeParam) return null;

      const currentValue = getValue(name);
      const defaultValue = getDefaultValue(name);
      return parameter.removeParam({
        defaultValue,
        value: currentValue,
        parameters,
        removedValue: value,
        staticProps: props.staticProps,
      });
    },
    [props.schemaHolder, getValue, getDefaultValue, props.staticProps, parameters]
  );

  return {
    parameters,
    getValue,
    getParameter,
    getDefaultValue,
    removeQueryParameter,
    convertValueToUrlQueryString,
    convertUrlQueryStringToParam,
    convertValueToSearchQuery,
    sanitizeQueryParameter,
  };
}
